import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays, Trophy, Check, X, Clock, ShieldCheck, Star, Users, Zap, Sparkles, IndianRupee } from 'lucide-react';
import DesktopHeader from '../components/DesktopHeader';
import BottomNav from '../components/BottomNav';
import { useApp } from '../context/AppContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authCfg = () => {
  const token = localStorage.getItem('c24_token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  };
};

const PLAN_DETAILS = {
  weekly: {
    tagline: 'Perfect for busy homes that need a light weekly refresh.',
    heroBg: 'linear-gradient(135deg, #2E7D32 0%, #43A047 100%)',
    accent: '#2E7D32',
    bg: '#E8F5E9',
    icon: CalendarDays,
    included: [
      '4 cleaning visits per month',
      '3 hours per visit (12 hours/month total)',
      'Same-day slot preference',
      'Verified & background-checked cleaner',
      'Live tracking on booking day',
      'All cleaning supplies included',
    ],
    excluded: ['Deep cleaning / stain removal', 'Water tank / glass cleaning', 'Priority slot booking'],
    faqs: [
      { q: 'Can I choose the day of the week?', a: 'Yes. When you subscribe you pick a preferred weekday and time slot. You can reschedule any visit up to 2 hours before.' },
      { q: 'What if I miss a week?', a: 'Missed visits do not carry over to the next month, but you can pause your plan for up to 2 weeks any time.' },
      { q: 'Can I upgrade later?', a: 'Yes — upgrade to Monthly or Gold any time; the price difference is prorated.' },
    ],
  },
  monthly: {
    tagline: 'Twice-a-week freshness for larger homes & busy families.',
    heroBg: 'linear-gradient(135deg, #2E7D32 0%, #43A047 100%)',
    accent: '#2E7D32',
    bg: '#E8F5E9',
    icon: CalendarDays,
    included: [
      '8 cleaning visits per month',
      '3 hours per visit (24 hours/month total)',
      'Twice-a-week schedule',
      'Same verified cleaner every visit',
      'Priority slot booking',
      'All cleaning supplies included',
      '10% off any additional bookings',
    ],
    excluded: ['Post-renovation cleaning', 'Underground sump cleaning'],
    faqs: [
      { q: 'Do I always get the same cleaner?', a: 'Yes — we assign a dedicated cleaner. If they are on leave, we send an equally-qualified backup.' },
      { q: 'Can I choose the two weekdays?', a: 'Yes. Pick any two weekdays and a fixed time slot when subscribing.' },
      { q: 'Can I gift this plan?', a: 'Yes! Reach out on WhatsApp / +91 90000 12345 and we\'ll set it up as a gift.' },
    ],
  },
  gold: {
    tagline: 'The premium membership — save on everything, priority everywhere.',
    heroBg: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)',
    accent: '#EA580C',
    bg: '#FEF3C7',
    icon: Trophy,
    included: [
      'Flat 10% off ALL Clean24 bookings',
      'Priority slot booking (front of queue)',
      'Free monthly service audit',
      'Dedicated Gold customer-care line',
      '24/7 booking (including holidays)',
      'One free deep-clean visit / year (worth ₹1499)',
      'Early access to new services',
      'Exclusive Gold-only offers & referral rewards',
    ],
    excluded: ['This is a membership only, individual visits still charged (with 10% off)'],
    faqs: [
      { q: 'Is Gold a subscription for cleaning visits?', a: 'No — Gold is a membership that gives you 10% off every booking, priority slots, and premium support. Visits are booked and paid per-service (with the discount applied).' },
      { q: 'Can Gold be combined with Weekly / Monthly plans?', a: 'Yes. Gold stacks on top of any subscription — you\'ll get 10% off any add-on bookings.' },
      { q: 'What is the free deep-clean?', a: 'Once every 12 months you can redeem a free 3-hour deep-clean visit worth ₹1499.' },
    ],
  },
};

const SubscriptionDetail = () => {
  const { planId } = useParams();
  const nav = useNavigate();
  const { user, cart, addToCart } = useApp();
  const [plan, setPlan] = useState(null);
  const [mySub, setMySub] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/subscriptions/plans`),
      user ? axios.get(`${API}/subscriptions/mine`, authCfg()) : Promise.resolve({ data: {} }),
    ]).then(([p, s]) => {
      setPlan(p.data.find((x) => x.id === planId));
      setMySub(s.data && s.data.id ? s.data : null);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [planId, user]);

  const subscribe = () => {
    if (!user) { nav('/login'); return; }
    const cartId = `sub_${plan.id}`;
    const already = cart.find((c) => c.id === cartId);
    if (!already) {
      addToCart({
        id: cartId,
        name: `${plan.name} · Subscription`,
        price: plan.price,
        image: plan.premium
          ? 'https://images.unsplash.com/photo-1607082352121-fa243f3dde32?w=200'
          : 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=200',
        type: 'subscription',
        plan_id: plan.id,
        plan_name: plan.name,
        period: plan.period,
      });
    }
    nav('/checkout');
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading…</div>;
  if (!plan) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-2">
      <p className="text-neutral-600">Plan not found</p>
      <button onClick={() => nav('/subscriptions')} className="text-[#2E7D32] font-semibold text-sm">← Back to plans</button>
    </div>
  );

  const details = PLAN_DETAILS[planId] || PLAN_DETAILS.weekly;
  const Icon = details.icon;
  const isCurrent = mySub && mySub.plan_id === planId;

  return (
    <div className="min-h-screen bg-neutral-50 pb-28 md:pb-8">
      <DesktopHeader />

      {/* Mobile back header */}
      <div className="md:hidden sticky top-0 z-30 bg-white border-b border-neutral-100">
        <div className="px-5 py-4 flex items-center gap-3">
          <button onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-base font-extrabold truncate">{plan.name}</h1>
        </div>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden text-white" style={{ background: details.heroBg }}>
        <div className="max-w-[1000px] mx-auto px-5 md:px-8 py-8 md:py-12 relative z-10">
          <button onClick={() => nav('/subscriptions')} className="hidden md:inline-flex items-center gap-1 text-sm text-white/90 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" /> All plans
          </button>
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
              <Icon className="w-8 h-8 text-white" />
            </div>
            <div>
              {plan.premium && <span className="inline-block bg-white text-[#EA580C] text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">Premium Membership</span>}
              <h1 className="mt-1 text-3xl md:text-4xl font-extrabold leading-tight">{plan.name}</h1>
              <p className="mt-1 text-white/90 text-sm md:text-base max-w-lg">{details.tagline}</p>
            </div>
          </div>

          <div className="mt-6 flex items-baseline gap-2">
            <IndianRupee className="w-7 h-7" strokeWidth={2.5} />
            <span className="text-5xl font-extrabold">{plan.price}</span>
            <span className="text-white/85 text-lg">/{plan.period}</span>
          </div>
          <p className="text-white/85 text-sm mt-1">{plan.description}</p>
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-5 md:px-8 py-6 md:py-8 grid md:grid-cols-3 gap-4">
        {/* LEFT — details */}
        <div className="md:col-span-2 space-y-4">
          {/* What's included */}
          <section className="bg-white border border-neutral-200 rounded-xl p-5">
            <h2 className="font-bold text-lg">What's included</h2>
            <ul className="mt-3 space-y-2">
              {details.included.map((i, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-neutral-700">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: details.accent }} />
                  <span>{i}</span>
                </li>
              ))}
            </ul>

            {details.excluded?.length > 0 && (
              <>
                <h3 className="mt-4 font-bold text-sm text-neutral-500 uppercase tracking-wide">Not included</h3>
                <ul className="mt-2 space-y-1.5">
                  {details.excluded.map((i, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-neutral-500">
                      <X className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400" />
                      <span>{i}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          {/* How it works */}
          <section className="bg-white border border-neutral-200 rounded-xl p-5">
            <h2 className="font-bold text-lg">How it works</h2>
            <div className="mt-3 space-y-3">
              <Step n="1" title="Subscribe" text="Pick this plan, choose your preferred day & slot, and pay for the first period only." accent={details.accent} bg={details.bg} />
              <Step n="2" title="Get a dedicated cleaner" text="A verified professional is assigned to your home. Same person each visit." accent={details.accent} bg={details.bg} />
              <Step n="3" title="Track & manage" text="See visits used, reschedule, pause or cancel any time from the app." accent={details.accent} bg={details.bg} />
              <Step n="4" title="Auto-renew" text="Your plan renews automatically on the renewal date. Cancel anytime." accent={details.accent} bg={details.bg} />
            </div>
          </section>

          {/* Trust */}
          <section className="grid grid-cols-3 gap-2 md:gap-3">
            <Trust icon={ShieldCheck} label="100% Guarantee" desc="Re-clean free" accent={details.accent} bg={details.bg} />
            <Trust icon={Clock} label="On-time" desc="Or free service" accent={details.accent} bg={details.bg} />
            <Trust icon={Star} label={`${plan.premium ? '4.9★ Gold rated' : '4.9★ rated'}`} desc="10k+ bookings" accent={details.accent} bg={details.bg} />
          </section>

          {/* FAQs */}
          <section className="bg-white border border-neutral-200 rounded-xl p-5">
            <h2 className="font-bold text-lg">Frequently asked</h2>
            <div className="mt-3 space-y-2">
              {details.faqs.map((f, i) => (
                <div key={i} className="border border-neutral-100 rounded-lg">
                  <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-neutral-50">
                    <span className="font-semibold text-sm">{f.q}</span>
                    <span className="text-neutral-400 text-lg leading-none">{openFaq === i ? '−' : '+'}</span>
                  </button>
                  {openFaq === i && <p className="px-4 pb-3 text-sm text-neutral-600 border-t border-neutral-100 pt-2">{f.a}</p>}
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* RIGHT — sticky subscribe card */}
        <aside>
          <div className="md:sticky md:top-24 bg-white border border-neutral-200 rounded-xl p-5">
            <p className="text-xs font-bold text-neutral-500 uppercase tracking-wider">You pay</p>
            <div className="mt-1 flex items-baseline gap-1">
              <span className="text-3xl font-extrabold">₹{plan.price}</span>
              <span className="text-neutral-500 text-sm">/{plan.period}</span>
            </div>
            <p className="text-xs text-neutral-500 mt-0.5">Cancel any time · No hidden fees</p>

            <div className="mt-4 pt-4 border-t border-neutral-100 space-y-1.5 text-xs text-neutral-600">
              <p className="flex items-center gap-2"><Check className="w-3.5 h-3.5" style={{ color: details.accent }} /> Verified professionals</p>
              <p className="flex items-center gap-2"><Check className="w-3.5 h-3.5" style={{ color: details.accent }} /> Same cleaner each visit</p>
              <p className="flex items-center gap-2"><Check className="w-3.5 h-3.5" style={{ color: details.accent }} /> Live tracking</p>
            </div>

            <button onClick={subscribe} disabled={isCurrent}
              data-testid="subscription-detail-subscribe-btn"
              className={`mt-4 w-full py-3 rounded-xl font-semibold text-sm transition-colors ${isCurrent
                ? 'bg-neutral-200 text-neutral-500 cursor-not-allowed'
                : plan.premium
                  ? 'bg-[#EA580C] hover:bg-[#C2410C] text-white'
                  : 'bg-[#2E7D32] hover:bg-[#276D2A] text-white'}`}>
              {isCurrent ? 'Current plan' : `Subscribe · ₹${plan.price}/${plan.period === 'week' ? 'wk' : 'mo'}`}
            </button>

            <p className="mt-3 text-center text-[11px] text-neutral-500">
              By subscribing you agree to our <button onClick={() => nav('/terms')} className="underline hover:text-neutral-800">Terms</button> and <button onClick={() => nav('/refunds')} className="underline hover:text-neutral-800">Refund policy</button>.
            </p>
          </div>
        </aside>
      </div>

      {/* Mobile sticky CTA */}
      <div className="md:hidden fixed bottom-16 left-0 right-0 bg-white border-t border-neutral-100 px-4 py-3 z-30 flex items-center gap-3">
        <div>
          <p className="text-xs text-neutral-500">Total</p>
          <p className="font-bold text-lg" style={{ color: details.accent }}>₹{plan.price}<span className="text-xs text-neutral-500 font-normal">/{plan.period}</span></p>
        </div>
        <button onClick={subscribe} disabled={isCurrent}
          data-testid="subscription-detail-mobile-subscribe-btn"
          className={`ml-auto py-2.5 px-5 rounded-lg font-semibold text-sm ${isCurrent ? 'bg-neutral-200 text-neutral-500' : plan.premium ? 'bg-[#EA580C] text-white' : 'bg-[#2E7D32] text-white'}`}>
          {isCurrent ? 'Current plan' : 'Subscribe'}
        </button>
      </div>

      <BottomNav />
    </div>
  );
};

const Step = ({ n, title, text, accent, bg }) => (
  <div className="flex items-start gap-3">
    <span className="w-7 h-7 rounded-full text-white text-sm font-bold flex items-center justify-center flex-shrink-0" style={{ background: accent }}>{n}</span>
    <div>
      <p className="font-bold text-sm">{title}</p>
      <p className="text-xs text-neutral-600 mt-0.5">{text}</p>
    </div>
  </div>
);

const Trust = ({ icon: Icon, label, desc, accent, bg }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-3 text-center">
    <div className="w-9 h-9 rounded-full flex items-center justify-center mx-auto" style={{ background: bg }}>
      <Icon className="w-4 h-4" style={{ color: accent }} />
    </div>
    <p className="mt-2 text-xs font-bold text-neutral-900 leading-tight">{label}</p>
    <p className="text-[10px] text-neutral-500 mt-0.5">{desc}</p>
  </div>
);

export default SubscriptionDetail;
